<template>
  <div class="infra-page">
    <header class="infra-cabecera">
      <div>
        <h1 class="infra-titulo">Infraestructura (Hostinger)</h1>
        <p class="infra-subtitulo">
          Nodos/datacenters y cron jobs de la cuenta real de hosting, vía la API de Hostinger.
        </p>
      </div>
    </header>

    <div v-if="avisoEsquema" class="infra-aviso">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      Esta integración es nueva: si una acción falla, el mensaje de error de Hostinger se muestra
      abajo tal cual, para poder ajustar el formato del pedido sin adivinar.
    </div>

    <!-- Nodos / Datacenters -->
    <section class="infra-seccion">
      <div class="infra-seccion__header">
        <h2>Nodos / Datacenters</h2>
        <button class="infra-boton infra-boton--secundario" @click="cargarDatacenters" :disabled="cargandoDc">
          <i class="bi bi-arrow-clockwise"></i> Actualizar
        </button>
      </div>
      <div v-if="cargandoDc" class="infra-info">Cargando...</div>
      <div v-else-if="errorDc" class="infra-error">{{ errorDc }}</div>
      <div v-else-if="!datacenters.length" class="infra-info">Sin datos.</div>
      <table v-else class="infra-tabla">
        <thead><tr><th v-for="(v, k) in datacenters[0]" :key="k">{{ k }}</th></tr></thead>
        <tbody>
          <tr v-for="(dc, i) in datacenters" :key="i">
            <td v-for="(v, k) in dc" :key="k">{{ v }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- Cron Jobs -->
    <section class="infra-seccion">
      <div class="infra-seccion__header">
        <h2>Cron Jobs</h2>
        <div class="d-flex gap-2">
          <button class="infra-boton infra-boton--secundario" @click="cargarCronJobs" :disabled="cargandoCron">
            <i class="bi bi-arrow-clockwise"></i> Actualizar
          </button>
          <button class="infra-boton infra-boton--primario" @click="modalCron = { schedule: '', command: '', name: '' }">
            <i class="bi bi-plus-lg"></i> Nuevo cron job
          </button>
        </div>
      </div>
      <div v-if="cargandoCron" class="infra-info">Cargando...</div>
      <div v-else-if="errorCron" class="infra-error">{{ errorCron }}</div>
      <div v-else-if="!cronJobs.length" class="infra-info">No hay cron jobs registrados.</div>
      <table v-else class="infra-tabla">
        <thead>
          <tr>
            <th v-for="(v, k) in cronJobs[0]" :key="k">{{ k }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(cj, i) in cronJobs" :key="i">
            <td v-for="(v, k) in cj" :key="k">{{ v }}</td>
            <td>
              <button class="infra-boton infra-boton--xs infra-boton--peligro" @click="eliminarCron(cj)">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- Modal crear cron job -->
    <div v-if="modalCron" class="infra-modal-fondo" @click.self="modalCron = null">
      <div class="infra-modal">
        <h2>Nuevo cron job</h2>
        <label>Nombre (opcional)<input v-model="modalCron.name" class="infra-input" placeholder="Ej: limpieza de logs"/></label>
        <label>Expresión cron<input v-model="modalCron.schedule" class="infra-input" placeholder="0 5 * * *"/></label>
        <label>Comando / URL<input v-model="modalCron.command" class="infra-input" placeholder="php /home/.../script.php"/></label>
        <div v-if="errorModalCron" class="infra-error">{{ errorModalCron }}</div>
        <div class="infra-modal__acciones">
          <button class="infra-boton infra-boton--secundario" @click="modalCron = null">Cancelar</button>
          <button class="infra-boton infra-boton--primario" @click="guardarCron" :disabled="guardandoCron">Crear</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import hostinger from '@/services/hostingerService'
import { notifySuccess, notifyError } from '@/services/notificationService'

export default {
  name: 'AdminInfraestructura',
  data() {
    return {
      avisoEsquema: true,
      datacenters: [],
      cargandoDc: false,
      errorDc: '',
      cronJobs: [],
      cargandoCron: false,
      errorCron: '',
      modalCron: null,
      guardandoCron: false,
      errorModalCron: '',
    }
  },
  mounted() {
    this.cargarDatacenters()
    this.cargarCronJobs()
  },
  methods: {
    async cargarDatacenters() {
      this.cargandoDc = true
      this.errorDc = ''
      try {
        const res = await hostinger.listarDatacenters()
        this.datacenters = Array.isArray(res.datos) ? res.datos : []
      } catch (e) {
        this.errorDc = e?.response?.data?.mensaje || 'No se pudo cargar la lista de nodos/datacenters.'
      } finally { this.cargandoDc = false }
    },
    async cargarCronJobs() {
      this.cargandoCron = true
      this.errorCron = ''
      try {
        const res = await hostinger.listarCronJobs()
        this.cronJobs = Array.isArray(res.datos) ? res.datos : []
      } catch (e) {
        this.errorCron = e?.response?.data?.mensaje || 'No se pudo cargar la lista de cron jobs.'
      } finally { this.cargandoCron = false }
    },
    async guardarCron() {
      const c = this.modalCron
      if (!c.schedule?.trim() || !c.command?.trim()) {
        this.errorModalCron = 'La expresión cron y el comando son obligatorios.'
        return
      }
      this.guardandoCron = true
      this.errorModalCron = ''
      try {
        await hostinger.crearCronJob(c)
        notifySuccess('Cron job creado.')
        this.modalCron = null
        await this.cargarCronJobs()
      } catch (e) {
        this.errorModalCron = e?.response?.data?.mensaje || 'Error al crear el cron job.'
      } finally { this.guardandoCron = false }
    },
    async eliminarCron(cj) {
      const id = cj.id ?? cj.uuid
      if (!id) { notifyError('No se pudo determinar el ID del cron job.'); return }
      try {
        await hostinger.eliminarCronJob(id)
        notifySuccess('Cron job eliminado.')
        await this.cargarCronJobs()
      } catch (e) {
        notifyError(e?.response?.data?.mensaje || 'No se pudo eliminar.')
      }
    },
  },
}
</script>

<style scoped>
.infra-page { color: var(--text-primary); }
.infra-cabecera { margin-bottom: 14px; }
.infra-titulo { font-size: 1.4rem; margin: 0; }
.infra-subtitulo { margin: 4px 0 0; color: var(--text-secondary); font-size: 0.9rem; }

.infra-aviso {
  background: color-mix(in srgb, var(--accent-warning) 15%, transparent);
  color: var(--accent-warning);
  border: 1px solid color-mix(in srgb, var(--accent-warning) 40%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.85rem;
  margin-bottom: 18px;
}

.infra-seccion {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 18px;
  box-shadow: var(--shadow-sm);
}
.infra-seccion__header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.infra-seccion__header h2 { font-size: 1.05rem; margin: 0; }

.infra-info { color: var(--text-secondary); padding: 10px 0; }
.infra-error {
  color: var(--accent-danger);
  background: color-mix(in srgb, var(--accent-danger) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent-danger) 35%, transparent);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.85rem;
  white-space: pre-wrap;
}

.infra-tabla { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.infra-tabla th, .infra-tabla td {
  text-align: left;
  padding: 6px 10px;
  border-bottom: 1px solid var(--border-main);
  color: var(--text-primary);
}
.infra-tabla th { color: var(--text-secondary); font-weight: 600; }

.infra-boton {
  border: 1px solid var(--border-main);
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.infra-boton:disabled { opacity: 0.6; cursor: not-allowed; }
.infra-boton--xs { padding: 4px 8px; font-size: 0.78rem; }
.infra-boton--primario { background: var(--primary-main); border-color: var(--primary-main); color: #fff; }
.infra-boton--secundario { background: transparent; }
.infra-boton--peligro { color: var(--accent-danger); border-color: color-mix(in srgb, var(--accent-danger) 40%, transparent); }

.infra-modal-fondo { position: fixed; inset: 0; z-index: 2000; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; }
.infra-modal {
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  border-radius: 12px;
  padding: 22px;
  min-width: 420px;
  max-width: 560px;
  border: 1px solid var(--border-main);
  box-shadow: var(--shadow-md);
}
.infra-modal h2 { font-size: 1.1rem; margin: 0 0 16px; }
.infra-modal label { display: block; margin-bottom: 10px; font-size: 0.85rem; color: var(--text-secondary); }
.infra-modal__acciones { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }

.infra-input {
  width: 100%;
  background: var(--bg-main);
  color: var(--text-primary);
  border: 1px solid var(--border-main);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.92rem;
  margin-top: 4px;
  font-family: inherit;
}
.infra-input:focus { outline: none; border-color: var(--border-focus); }
</style>
